import { z } from 'zod';
import { PersonRef } from './people.js';
/**
 * Repositories (Bitbucket, GitHub, GitLab), the brief's "Repositories" feature module (docs/adr/0052-repositories.md).
 * A project connects a repository by pasting a per-repository webhook URL and secret into the provider; pushes, pull
 * requests and releases then show in the project's Repository feed, and a task key in a commit message, pull request
 * title or branch links the commit or pull request to that task. Metadata only: ids, titles, author names, branches,
 * links and times. Never code, diffs or full commit messages.
 */
export const RepoProvider = z.enum(['github', 'gitlab', 'bitbucket']);
export const REPO_PROVIDER_LABEL = { github: 'GitHub', gitlab: 'GitLab', bitbucket: 'Bitbucket' };
/** "owner/repo", "group/subgroup/repo" or "workspace/repo": as people see it on the provider. */
export const RepoName = z
    .string()
    .trim()
    .min(3)
    .max(200)
    .regex(/^[A-Za-z0-9_.-]+(\/[A-Za-z0-9_.-]+){1,5}$/, 'Use the name as it appears on the provider, like acme/website');
export const Repository = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    provider: RepoProvider,
    name: z.string(),
    /** Where the provider sends events. Owners and admins only (null for members). */
    webhookUrl: z.string().nullable(),
    /** The last few characters of the secret, so people can tell which one the provider has. Owners and admins only. */
    secretHint: z.string().nullable(),
    secretRotatedAt: z.string(),
    /** When the provider last reached Chapsi with a valid signature (null until it does). */
    lastDeliveryAt: z.string().nullable(),
    connectedBy: PersonRef.nullable(),
    createdAt: z.string(),
});
export const RepositoryList = z.object({
    items: z.array(Repository),
    /** Whether the caller may connect, rotate and disconnect (owners and admins). */
    canManage: z.boolean(),
});
export const ConnectRepositoryInput = z.object({ provider: RepoProvider, name: RepoName });
/** The secret is shown once, here, and never again: rotating makes a new one. */
export const RepositoryWithSecret = z.object({ repository: Repository, secret: z.string() });
export const RepoCommit = z.object({
    sha: z.string(),
    /** The first line of the commit message only. */
    title: z.string(),
    url: z.string().nullable(),
    authorName: z.string(),
});
export const RepoEventKind = z.enum(['push', 'pull_request', 'release']);
export const RepoEventAction = z.enum(['pushed', 'opened', 'reopened', 'merged', 'closed', 'published']);
export const LinkedTask = z.object({ id: z.uuid(), key: z.string(), title: z.string() });
/** One entry of a project's Repository feed. */
export const RepoActivity = z.object({
    id: z.uuid(),
    repositoryId: z.uuid(),
    repositoryName: z.string(),
    provider: RepoProvider,
    kind: RepoEventKind,
    action: RepoEventAction,
    /** Pull request title or release name; empty for a push. */
    title: z.string(),
    /** Branch (push, pull request source) or tag (release). */
    ref: z.string().nullable(),
    /** Pull requests: the branch it merges into. */
    targetRef: z.string().nullable(),
    number: z.number().int().nullable(),
    url: z.string().nullable(),
    authorName: z.string(),
    commitCount: z.number().int(),
    /** Up to 20 commits of a push, newest last. */
    commits: z.array(RepoCommit),
    /** Tasks this event links to, that the caller can see. */
    tasks: z.array(LinkedTask),
    occurredAt: z.string(),
});
export const RepoFeedQuery = z.object({
    /** The id of the oldest entry already shown: the next page starts after it. */
    before: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(50).optional(),
});
export const RepoFeed = z.object({ items: z.array(RepoActivity), hasMore: z.boolean() });
/** A commit or pull request linked to a task ("Commits and pull requests" on the task drawer). */
export const TaskRepoLink = z.object({
    id: z.uuid(),
    kind: z.enum(['commit', 'pull_request']),
    provider: RepoProvider,
    repositoryName: z.string(),
    /** Short commit sha, or "#12" for a pull request. */
    label: z.string(),
    title: z.string(),
    url: z.string().nullable(),
    authorName: z.string(),
    branch: z.string().nullable(),
    state: z.enum(['pushed', 'open', 'merged', 'closed']),
    occurredAt: z.string(),
});
export const TaskRepoLinks = z.object({ items: z.array(TaskRepoLink) });
export const ChannelRepoActivityInput = z.object({ enabled: z.boolean() });
/** Webhook bodies are capped (docs/adr/0052): a bigger delivery answers 413 and is not stored. */
export const REPO_WEBHOOK_MAX_BYTES = 1024 * 1024;
/** Commits kept per push for the feed, and task keys followed per event. */
export const REPO_COMMITS_KEPT = 20;
export const REPO_KEYS_PER_EVENT = 10;
const KEY_IN_TEXT = /(?<![A-Za-z0-9-])([A-Z][A-Z0-9]{1,5}|T)-([1-9][0-9]{0,6})(?![A-Za-z0-9])/g;
const KEY_IN_BRANCH = /(?<![A-Za-z0-9])([A-Za-z][A-Za-z0-9]{1,5}|[Tt])-([1-9][0-9]{0,6})(?![0-9])/g;
/**
 * Task keys mentioned in commit messages and pull request titles ("HPA-9", "T-12": upper case, as Chapsi
 * shows them) and in branch names ("feature/hpa-9-login": any case, since branches are often lower case).
 * Support tickets ("#1001") are never linked. Returns unique upper-case keys, first mention first, at most
 * REPO_KEYS_PER_EVENT.
 */
export function extractTaskKeys(texts, branches = []) {
    const out = [];
    const add = (prefix, n) => {
        const key = `${prefix.toUpperCase()}-${n}`;
        if (!out.includes(key) && out.length < REPO_KEYS_PER_EVENT)
            out.push(key);
    };
    for (const t of texts)
        for (const m of (t ?? '').slice(0, 10_000).matchAll(KEY_IN_TEXT))
            add(m[1], m[2]);
    for (const b of branches)
        for (const m of (b ?? '').slice(0, 255).matchAll(KEY_IN_BRANCH))
            add(m[1], m[2]);
    return out;
}
/** Splits "HPA-9" into its project key and number ("T" means a task without a project). */
export function parseTaskKey(key) {
    const m = /^([A-Z][A-Z0-9]{0,5})-([1-9][0-9]{0,6})$/.exec(key);
    return m ? { prefix: m[1], number: Number(m[2]) } : null;
}
// --- Commit nudges and the review queue (docs/adr/0059-commit-nudges.md) ------------------------------------
/** A username on GitHub, GitLab or Bitbucket. Compared lower-case (providerUsername). */
export const ProviderUsername = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/, 'Use your username as it appears on the provider, like ana-lee');
/** Normalises a username (from a payload or a person's setting) to lower case, or null when it is not one. */
export function providerUsername(v) {
    const parsed = ProviderUsername.safeParse(typeof v === 'string' ? v : '');
    return parsed.success ? parsed.data.toLowerCase() : null;
}
/** A project's commit nudge settings, as owners and admins edit them on the Repository tab. */
export const CommitNudgeSettings = z.object({
    /** Working hours without a linked commit before the assignee is nudged; 0 is off. Null: the workspace's value. */
    afterHours: z.number().int().min(0).max(168).nullable(),
    /** The workspace's value (or the default), shown when the project does not set its own. */
    workspaceAfterHours: z.number().int(),
    /** Who gets the second nudge; null is off (the default). */
    lead: PersonRef.nullable(),
    /** The project's rule for merged pull requests; null: the workspace's. */
    mergedPullRequestMovesTo: z.enum(['off', 'review', 'done']).nullable(),
    workspaceMergedPullRequestMovesTo: z.enum(['off', 'review', 'done']),
    /** Owners and admins may change them. */
    canManage: z.boolean(),
});
export const UpdateCommitNudgeSettingsInput = z.object({
    afterHours: z.number().int().min(0).max(168).nullable().optional(),
    leadUserId: z.uuid().nullable().optional(),
    mergedPullRequestMovesTo: z.enum(['off', 'review', 'done']).nullable().optional(),
});
/** A pull request that asks the caller for a review ("Waiting for your review" on My work). */
export const ReviewQueueItem = z.object({
    repositoryId: z.uuid(),
    repositoryName: z.string(),
    provider: RepoProvider,
    projectId: z.uuid(),
    number: z.number().int(),
    title: z.string(),
    url: z.string().nullable(),
    authorName: z.string(),
    branch: z.string().nullable(),
    requestedAt: z.string(),
});
export const ReviewQueue = z.object({
    items: z.array(ReviewQueueItem),
    /** Whether the caller told Chapsi their provider usernames (Your account → Notifications). */
    hasUsernames: z.boolean(),
});
/** Requested reviewers kept per pull request, and pull requests listed in the queue. */
export const REVIEWERS_PER_PULL_REQUEST = 20;
export const REVIEW_QUEUE_MAX = 50;
//# sourceMappingURL=repos.js.map